import { useState, useEffect } from 'react';
import { variablesTema, cargarFuente } from '../tema';
import { useParams } from 'react-router-dom';
import { fmtDate } from '../events/util';

const API = process.env.REACT_APP_API_URL || '';

// Anotarse en la lista de cumpleaños. Es una PÁGINA propia y no una ventanita dentro del
// evento a propósito: así tiene URL propia y se puede pegar en una historia de Instagram.
// Por eso también muestra de qué fiesta se trata — el que llega desde la historia no vio
// la página del evento.

// '2026-08-31' -> '31 de agosto'. Se parte el string en vez de usar Date: `new Date('2026-08-31')`
// es medianoche UTC y en Argentina se muestra como el 30. Ver [[zona-horaria]].
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
export function diaYMes(iso) {
  if (!iso) return '';
  const [, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return `${d} de ${MESES[m - 1] || ''}`;
}

export default function CumpleForm() {
  const { eventId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [f, setF] = useState({ last_name: '', first_name: '', birthdate: '', dni: '' });
  const [enviando, setEnviando] = useState(false);
  const [err, setErr] = useState('');
  const [listo, setListo] = useState(null);   // { yaEstaba }
  // Se llenó entre que cargó la página y apretó el botón. Es raro pero pasa cuando se
  // comparte el link en una historia: en lugar de un error en rojo abajo del formulario,
  // se muestra la misma pantalla de agotada que ve el que llega tarde.
  const [agotada, setAgotada] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/public/event/${eventId}`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(d => { setData(d); cargarFuente(d && d.event && d.event.tipografia); })
      .catch(() => setError('No encontramos este evento.'));
  }, [eventId]);

  const set = (k, v) => setF(s => ({ ...s, [k]: v }));

  async function enviar() {
    setErr(''); setEnviando(true);
    try {
      const r = await fetch(`${API}/api/public/event/${eventId}/cumple`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(f),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (d.code === 'LLENA') { setAgotada(true); return; }
        setErr(d.error || 'No se pudo anotar.'); return;
      }
      setListo({ yaEstaba: !!d.yaEstaba });
    } catch { setErr('Error de conexión.'); } finally { setEnviando(false); }
  }

  if (error) return <Shell><P>{error}</P></Shell>;
  if (!data) return <Shell><P>Cargando…</P></Shell>;

  const ev = data.event, c = data.cumple;
  const volver = `/e/${eventId}`;

  if (!c) return (
    <Shell org={ev.org_name} tema={variablesTema(ev)}>
      <H>{ev.name}</H>
      <P>Este evento no tiene lista de cumpleaños.</P>
      <a className="lb-btn lb-btn--ghost lb-btn--sm lb-btn--block" href={volver} style={{ marginTop: 16 }}>Ver el evento</a>
    </Shell>
  );

  // Cerrada: se dice, no se esconde. Alguien que llega tarde desde una historia vieja
  // tiene que entender por qué no puede anotarse, no encontrarse una pantalla vacía.
  if (!c.abierto) return (
    <Shell org={ev.org_name} tema={variablesTema(ev)}>
      <H>{ev.name}</H>
      <Beneficio c={c} />
      <div style={{ marginTop: 16, padding: '14px 16px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--ink-050)', fontSize: 13, color: 'var(--ink-700)', lineHeight: 1.5 }}>
        La lista ya <b style={{ color: '#fff' }}>está cerrada</b> para esta fecha. El beneficio es sólo para quienes están anotados.
      </div>
      <a className="lb-btn lb-btn--ghost lb-btn--sm lb-btn--block" href={volver} style={{ marginTop: 16 }}>Ver el evento</a>
    </Shell>
  );

  if (listo) return (
    <Shell org={ev.org_name} tema={variablesTema(ev)}>
      <div style={{ fontSize: 44, textAlign: 'center' }}>🎂</div>
      <H style={{ textAlign: 'center' }}>{listo.yaEstaba ? 'Ya estabas en la lista' : '¡Listo, estás anotado!'}</H>
      <div style={{ fontSize: 13.5, color: 'var(--ink-700)', lineHeight: 1.6, textAlign: 'center', marginTop: 10 }}>
        {listo.yaEstaba
          ? <>Tu documento ya figuraba para <b style={{ color: '#fff' }}>{ev.name}</b>. No hace falta que hagas nada más.</>
          : <>Te esperamos en <b style={{ color: '#fff' }}>{ev.name}</b>.</>}
      </div>
      {/* Lo más importante de esta pantalla: que sepa que sin el documento no entra. */}
      <div style={{ marginTop: 18, padding: '14px 16px', borderRadius: 6, border: '1px solid var(--red-500)', background: 'rgba(139,15,26,0.12)', fontSize: 13, color: 'var(--ink-800)', lineHeight: 1.55 }}>
        Presentate en <b style={{ color: '#fff' }}>boletería</b> con tu <b style={{ color: '#fff' }}>documento físico</b>. Sin el documento no se puede dar el beneficio.
      </div>
      <a className="lb-btn lb-btn--primary lb-btn--sm lb-btn--block" href={volver} style={{ marginTop: 18 }}>Ver el evento</a>
    </Shell>
  );

  // Agotada: se avisa ANTES del formulario. Completar cuatro campos para que al final
  // le digan que no hay lugar es la peor versión de esta pantalla.
  if (c.lleno || agotada) return (
    <Shell org={ev.org_name} tema={variablesTema(ev)}>
      <div className="lb-eyebrow" style={{ marginBottom: 4 }}>{fmtDate(ev.date)}</div>
      <H>{ev.name}</H>
      <Beneficio c={c} />
      <div style={{ marginTop: 16, padding: '14px 16px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--ink-050)', fontSize: 13, color: 'var(--ink-700)', lineHeight: 1.5 }}>
        {/* Sin el número de lugares: cuántos cumpleañeros entran es un dato del boliche,
            no del que se quiere anotar, y decirlo sólo invita a discutirlo en la puerta. */}
        La lista de cumpleaños está <b style={{ color: '#fff' }}>COMPLETA</b> para esta fecha. El beneficio es sólo para quienes están anotados.
      </div>
      <a className="lb-btn lb-btn--primary lb-btn--sm lb-btn--block" href={volver} style={{ marginTop: 16 }}>Ver el evento</a>
    </Shell>
  );

  const completo = f.last_name.trim() && f.first_name.trim() && f.birthdate && f.dni.trim();

  return (
    <Shell org={ev.org_name} tema={variablesTema(ev)}>
      <div className="lb-eyebrow" style={{ marginBottom: 4 }}>{fmtDate(ev.date)}</div>
      <H>{ev.name}</H>
      <Beneficio c={c} />

      <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <input className="lb-input" placeholder="Apellido" value={f.last_name} onChange={e => set('last_name', e.target.value)} />
        <input className="lb-input" placeholder="Nombre" value={f.first_name} onChange={e => set('first_name', e.target.value)} />
        <div>
          <div className="lb-label" style={{ marginBottom: 5 }}>Fecha de nacimiento</div>
          <input className="lb-input" type="date" value={f.birthdate} onChange={e => set('birthdate', e.target.value)} style={{ width: '100%' }} />
        </div>
        <input className="lb-input" inputMode="numeric" placeholder="Número de documento" value={f.dni}
          onChange={e => set('dni', e.target.value.replace(/[^\d.]/g, '').slice(0, 11))} />
        {err && <div className="lb-err">{err}</div>}
        <button className="lb-btn lb-btn--primary lb-btn--block" onClick={enviar} disabled={enviando || !completo}>
          {enviando ? 'Anotando…' : 'Anotarme en la lista'}
        </button>
        <div style={{ fontSize: 11.5, color: 'var(--ink-600)', lineHeight: 1.5, textAlign: 'center' }}>
          En la puerta se verifica tu documento físico contra estos datos.
        </div>
      </div>
    </Shell>
  );
}

function Beneficio({ c }) {
  return (
    <div style={{ marginTop: 14, padding: '16px 18px', borderRadius: 6, border: '1px solid var(--red-500)', background: 'linear-gradient(180deg, rgba(139,15,26,0.18), rgba(139,15,26,0.04))' }}>
      <div className="lb-eyebrow" style={{ color: 'var(--red-400)' }}>Beneficio cumpleañeros</div>
      <div style={{ color: '#fff', fontWeight: 600, fontSize: 14, marginTop: 6 }}>
        Del {diaYMes(c.desde)} al {diaYMes(c.hasta)}
      </div>
      {c.beneficio && <div style={{ fontSize: 13.5, color: 'var(--ink-800)', marginTop: 6, lineHeight: 1.5 }}>{c.beneficio}</div>}
      <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 8, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Exclusivo mayores de 18 años</div>
    </div>
  );
}

const H = ({ children, style }) => <div className="lb-h" style={{ fontSize: 22, ...style }}>{children}</div>;
const P = ({ children }) => <div style={{ color: 'var(--ink-600)', textAlign: 'center', fontSize: 14 }}>{children}</div>;

function Shell({ children, org, tema }) {
  return (
    <div style={{ ...(tema || {}), minHeight: '100vh', background: 'var(--bg)', padding: '40px 20px' }}>
      <div style={{ maxWidth: 420, margin: '0 auto' }}>
        {org && <div className="lb-eyebrow" style={{ textAlign: 'center', marginBottom: 20 }}>{org}</div>}
        <div className="lb-card" style={{ padding: '26px 24px' }}>{children}</div>
      </div>
    </div>
  );
}
